import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { formatDateTime } from '../shared/formatting';

type NoteEvent = {
    kind: 'comment' | 'collab';
    id: string;
    context_key: string;
    user_id: number;
    user_name: string | null;
    preview: string;
    at: string;
    is_new: boolean;
    mention?: boolean;
    thread?: boolean;
};

type FeedPayload = {
    rows: NoteEvent[];
    last_seen_at: string | null;
    modules: string[];
};

/** context_key "sales/promet" → { module: "sales", tab: "promet" }. */
function splitContext(ctx: string): { module: string; tab: string | null } {
    const i = ctx.indexOf('/');
    if (i === -1) return { module: ctx, tab: null };
    return { module: ctx.slice(0, i), tab: ctx.slice(i + 1) };
}

export function ModuleNotesLogSection({
    token,
    locale,
    onSeen,
}: {
    token: string;
    locale: 'hr' | 'en';
    onSeen?: () => void;
}) {
    const def = adminSectionDefinition('170');
    const title = def ? adminSectionNavLabel(def, locale) : '170';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [payload, setPayload] = useState<FeedPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [onlyNew, setOnlyNew] = useState(false);
    const [onlyMe, setOnlyMe] = useState(false);
    const [moduleFilter, setModuleFilter] = useState('');
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const q = new URLSearchParams();
            if (onlyNew) q.set('only_new', '1');
            if (onlyMe) q.set('only_me', '1');
            if (moduleFilter) q.set('module', moduleFilter);
            if (search) q.set('q', search);
            const r = await api<FeedPayload>(`/module-help/notes-feed?${q}`, token);
            setPayload(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, onlyNew, onlyMe, moduleFilter, search]);

    useEffect(() => {
        void load();
    }, [load]);

    // Kad se sekcija otvori (prvi load gotov), označi pročitanim → badge se ugasi.
    const markSeen = useCallback(async () => {
        try {
            await api('/module-help/notes-seen', token, { method: 'POST' });
            onSeen?.();
        } catch {
            /* tiho */
        }
    }, [token, onSeen]);

    useEffect(() => {
        if (!loading && payload) {
            void markSeen();
        }
        // samo jednom nakon prvog uspješnog loada
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading === false && payload !== null]);

    const newCount = useMemo(() => (payload?.rows ?? []).filter((r) => r.is_new).length, [payload]);

    const openModule = (ctx: string) => {
        const { module, tab } = splitContext(ctx);
        const url = new URL(window.location.href);
        url.searchParams.delete('admin');
        url.searchParams.set('m', module);
        if (tab) url.searchParams.set('tab', tab);
        else url.searchParams.delete('tab');
        window.location.assign(url.toString());
    };

    return (
        <section className="card nested-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <h3 style={{ margin: 0 }}>📓 {title}</h3>
                {newCount > 0 ? (
                    <span style={{ background: '#1f7a36', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 700 }}>
                        {newCount} {t('novo', 'new')}
                    </span>
                ) : null}
                <button type="button" className="secondary compact" style={{ marginLeft: 'auto' }} onClick={() => void load()}>
                    ↻ {t('Osvježi', 'Refresh')}
                </button>
                <button type="button" className="secondary compact" onClick={() => void markSeen()}>
                    ✓ {t('Označi sve pročitanim', 'Mark all read')}
                </button>
            </div>

            <p className="muted" style={{ marginTop: 0, fontSize: '0.84rem' }}>
                {t(
                    'Svi komentari i izmjene zajedničkog dokumenta kroz module/tabove. Zeleno = novo od tvojeg zadnjeg čitanja (od drugih).',
                    'All comments and shared-doc edits across modules/tabs. Green = new since your last read (by others).',
                )}
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.86rem' }}>
                    <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} />
                    {t('Samo novo', 'Only new')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.86rem', color: '#1f7a36', fontWeight: 600 }}>
                    <input type="checkbox" checked={onlyMe} onChange={(e) => setOnlyMe(e.target.checked)} />
                    🔔 {t('Za mene', 'For me')}
                </label>
                <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} style={{ fontSize: '0.86rem' }}>
                    <option value="">{t('Svi moduli', 'All modules')}</option>
                    {(payload?.modules ?? []).map((m) => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
                <input
                    type="text"
                    placeholder={t('Pretraga teksta…', 'Search text…')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ fontSize: '0.86rem', minWidth: 200 }}
                />
            </div>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {error ? <p className="error">{error}</p> : null}
            {!loading && !error ? (
                (payload?.rows ?? []).length === 0 ? (
                    <p className="muted">{t('Nema bilješki.', 'No notes.')}</p>
                ) : (
                    <div className="table-wrap" style={{ maxHeight: '64vh', overflowY: 'auto' }}>
                        <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }}></th>
                                    <th>{t('Vrijeme', 'When')}</th>
                                    <th>{t('Tko', 'Who')}</th>
                                    <th>{t('Modul / tab', 'Module / tab')}</th>
                                    <th>{t('Tip', 'Type')}</th>
                                    <th>{t('Sadržaj', 'Content')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(payload?.rows ?? []).map((r) => (
                                    <tr key={r.id} style={r.is_new ? { background: 'rgba(31,122,54,0.06)' } : undefined}>
                                        <td style={{ textAlign: 'center' }}>
                                            {r.is_new ? <span title={t('novo', 'new')} style={{ color: '#1f7a36', fontSize: '1rem' }}>●</span> : null}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.at, locale)}</td>
                                        <td style={r.is_new ? { fontWeight: 700 } : undefined}>{r.user_name ?? `#${r.user_id}`}</td>
                                        <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.context_key}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {r.kind === 'comment' ? `💬 ${t('komentar', 'comment')}` : `📝 ${t('izmjena dok.', 'doc edit')}`}
                                            {r.mention ? <span title={t('Spomenuli su te', 'You were mentioned')} style={{ marginLeft: 4, background: '#b14040', color: '#fff', borderRadius: 8, padding: '0 6px', fontSize: '0.66rem', fontWeight: 700 }}>🔔 @ti</span> : null}
                                            {!r.mention && r.thread ? <span title={t('Pisao si u ovom threadu', 'You participated in this thread')} style={{ marginLeft: 4, background: '#1f7a36', color: '#fff', borderRadius: 8, padding: '0 6px', fontSize: '0.66rem', fontWeight: 700 }}>thread</span> : null}
                                        </td>
                                        <td style={{ maxWidth: 420 }}>{r.preview}</td>
                                        <td>
                                            <button type="button" className="secondary compact" onClick={() => openModule(r.context_key)}>
                                                {t('Otvori', 'Open')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}
        </section>
    );
}
